// Per-tab memory for list filters, tabs and periods, so a filter set on one
// page is still there after visiting another page, opening a detail, using
// the browser Back button or refreshing. sessionStorage on purpose: a new tab
// or a new sign-in starts from the defaults (logout clears it), and nothing a
// filter holds is worth keeping across sessions. Storage can be missing or
// throw (private mode, blocked site data), so every access degrades to the
// default instead of failing the page.
const PREFIX = 'tt:';

function storage() {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

// The stored value for `key`, passed through `sanitize(value, fallback)` so a
// stale or hand-edited entry can never put a page into a state it cannot
// render. Missing, unreadable or invalid JSON yields `fallback`.
export function readSession(key, fallback, sanitize) {
  const store = storage();
  if (!store) return fallback;
  let raw;
  try {
    raw = store.getItem(PREFIX + key);
  } catch {
    return fallback;
  }
  if (raw == null) return fallback;
  let value;
  try {
    value = JSON.parse(raw);
  } catch {
    return fallback;
  }
  return sanitize ? sanitize(value, fallback) : value;
}

export function writeSession(key, value) {
  const store = storage();
  if (!store) return;
  try {
    store.setItem(PREFIX + key, JSON.stringify(value));
  } catch {
    // Quota or blocked storage: the state just won't outlive this page.
  }
}

// Forget everything this module stored — on logout, so the next person or
// the next sign-in starts from the defaults.
export function clearSession() {
  const store = storage();
  if (!store) return;
  try {
    const keys = [];
    for (let i = 0; i < store.length; i += 1) {
      const key = store.key(i);
      if (key && key.startsWith(PREFIX)) keys.push(key);
    }
    keys.forEach((key) => store.removeItem(key));
  } catch {
    // Nothing to clean up if storage is unavailable.
  }
}

// Remembered filters belong to whoever set them: a different person signing
// in on this tab (after a session expiry, say) starts from the defaults.
// Returns true when the tab belonged to someone else, so the caller can also
// ignore the filters that previous owner left in the URL. A tab with no owner
// yet (a fresh tab, a shared link, after logout) is not a change.
export function claimSessionState(userId) {
  const owner = readSession('owner', null);
  if (owner === userId) return false;
  clearSession();
  writeSession('owner', userId);
  return owner != null;
}

// Sanitizers for the shapes the pages store.

// A plain object with the defaults' keys, each kept only when it has the same
// type as its default (arrays must hold strings). Unknown keys are dropped.
export function sanitizeShape(value, fallback) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return fallback;
  }
  const out = { ...fallback };
  for (const key of Object.keys(fallback)) {
    if (!(key in value)) continue;
    const def = fallback[key];
    const next = value[key];
    if (Array.isArray(def)) {
      if (Array.isArray(next) && next.every((v) => typeof v === 'string'))
        out[key] = next;
    } else if (def === null) {
      if (next === null || typeof next === 'string') out[key] = next;
    } else if (typeof next === typeof def) {
      out[key] = next;
    }
  }
  return out;
}

// `sanitizeShape`, plus fields limited to a fixed set of choices
// (`{ status: ['active', 'all'] }`): a value no longer offered falls back to
// that field's default, so a stale filter cannot hide every row.
export function shapeWithChoices(choices) {
  return (value, fallback) => {
    const out = { ...sanitizeShape(value, fallback) };
    for (const [key, allowed] of Object.entries(choices)) {
      if (!allowed.includes(out[key])) out[key] = fallback[key];
    }
    return out;
  };
}

// One of a fixed set of values.
export function oneOf(allowed) {
  return (value, fallback) => (allowed.includes(value) ? value : fallback);
}

// A table sort: { key, direction: 'asc' | 'desc' }, the key limited to `keys`
// when given (a removed column must not stay the sort).
export function sortOf(keys) {
  return (value, fallback) =>
    value &&
    typeof value.key === 'string' &&
    (!keys || keys.includes(value.key)) &&
    (value.direction === 'asc' || value.direction === 'desc')
      ? { key: value.key, direction: value.direction }
      : fallback;
}

// A remembered "YYYY-MM" month, or nothing.
export function sanitizeMonth(value, fallback) {
  return typeof value === 'string' && /^\d{4}-(0[1-9]|1[0-2])$/.test(value)
    ? value
    : fallback;
}
